import { FormHelperText, Grid, Switch, FormControlLabel, Chip, Divider } from '@material-ui/core';
import type { StyledComponentProps } from '@material-ui/core/styles';
import { Alert } from '@material-ui/lab';
import { constants } from 'helpers/constants';
import { EChargeType } from 'interfaces/IEditProduct';
import { EPartnerCode } from 'interfaces/IProduct';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import eduzzAccountsService from 'v2/services/eduzzAccount';
import { run } from 'validations/ruleRunner';

import type { User } from 'v2/interfaces/user';

import AlertsDefaultOptions from './AlertsDefaultOptions';
import Validations from './validations';
import { FormContent } from '../FormContent';
import styles from '../styles';

import type IProduct from 'interfaces/IProduct';
import type { DispatchProp } from 'react-redux';

interface IProps extends IPropsFromConnect, DispatchProp<any>, StyledComponentProps {
  loading: boolean;
  fade?: any;
  product?: IProduct;
  modifieds?: any[];
  editing?: any;
  handleChange?: any;
  copyFromOriginal?: any;
  currencyList: any;
  onChangeRequiredFields?: any;
  classes?: any;
}

interface IPropsFromConnect {
  currentUser: User;
}

interface IStates {
  showErrors: boolean;
  validationErrors: any;
  singlePaymentErrors: any;
  subscriptionErrors: any;
  showAlertPix: boolean;
  showAlertCard: boolean;
  showAlertBoleto: boolean;
  showAlertMultipleCards: boolean;
  showAlertEduzzBalance: boolean;
}

type PaymentMethod = {
  label: string;
  id: string;
  checked: boolean;
  helperText: string;
  show: boolean;
  error?: string;
  disabled?: boolean;
  chip?: string;
  stateAlert?: AlertsDefaultOptionsStateName;
};

export type AlertsDefaultOptionsStateName = 'showAlertPix' | 'showAlertCard' | 'showAlertBoleto' | 'showAlertMultipleCards' | 'showAlertEduzzBalance';
const paymentShouldConfirmToDisable = ['allows_boleto', 'allows_card', 'allows_multiple_cards', 'allows_pix', 'allows_eduzz_balance'];

class PaymentMethods extends Component<IProps, IStates> {
  constructor(props: IProps) {
    super(props);
    this.state = {
      showErrors: false,
      validationErrors: run(props.product, Validations),
      singlePaymentErrors: {},
      subscriptionErrors: {},
      showAlertPix: false,
      showAlertCard: false,
      showAlertBoleto: false,
      showAlertMultipleCards: false,
      showAlertEduzzBalance: false,
    };
  }

  errorFor = (field: any) => {
    return this.state.validationErrors[field] || '';
  };

  isValid = (field: any) => {
    return this.state.validationErrors[field] ? true : false;
  };

  onChangeRequired = (name: string, fields: any) => {
    let newState = this.state;
    newState[name] = fields;
    this.setState(newState);
  };

  onChangeStates = () => {
    let { product, onChangeRequiredFields } = this.props;
    const { singlePaymentErrors, subscriptionErrors } = this.state;
    let newValidations = this.state.validationErrors;
    if (product.charge_type === EChargeType.SINGLE) {
      newValidations = { ...singlePaymentErrors, ...run(product, Validations) };
    } else if (product.charge_type === EChargeType.SUBSCRIPTION) {
      newValidations = { ...subscriptionErrors, ...run(product, Validations) };
    } else {
      newValidations = { ...run(product, Validations) };
    }
    this.setState({ validationErrors: newValidations });
    if (onChangeRequiredFields) {
      onChangeRequiredFields('salesRequiredFields', newValidations ? Object.keys(newValidations).length : 0);
    }
  };

  handleToggleAlertVisibility = (stateName: AlertsDefaultOptionsStateName) => {
    const newState = { ...this.state };
    newState[stateName] = !newState[stateName];
    this.setState(newState);
  };

  handleConfirmBeforeChange = (fieldName: string) => {
    let fieldConfirmToggleState: AlertsDefaultOptionsStateName;

    if (fieldName === 'allows_boleto') fieldConfirmToggleState = 'showAlertBoleto';
    if (fieldName === 'allows_pix') fieldConfirmToggleState = 'showAlertPix';
    if (fieldName === 'allows_card') fieldConfirmToggleState = 'showAlertCard';
    if (fieldName === 'allows_multiple_cards') fieldConfirmToggleState = 'showAlertMultipleCards';
    if (fieldName === 'allows_eduzz_balance') fieldConfirmToggleState = 'showAlertEduzzBalance';

    this.handleToggleAlertVisibility(fieldConfirmToggleState);
  };

  componentDidUpdate(prevProps: IProps) {
    if (!this.props.loading && this.props.product !== prevProps.product) {
      this.onChangeStates();
    }
  }

  componentDidMount() {
    if (!this.props.loading) {
      this.onChangeStates();
    }
  }

  render() {
    const { loading, handleChange, product, classes } = this.props;

    const isAlumy = product.cnt_partner_cod === EPartnerCode.ALUMY;
    const isInfo = product.type === constants.INFO;
    const isEvent = product.type === constants.TICKET;
    const isSinglePayment = product.charge_type === EChargeType.SINGLE;
    const isOpenValuePayment = product?.charge_type === EChargeType.OTHER;
    const isSubscriptionPayment = product.charge_type === EChargeType.SUBSCRIPTION;
    const isFree = product.charge_type === EChargeType.FREE;
    const isPhysical = product?.type === constants.PHYSICAL;
    const showInternationalChip = isInfo && isSinglePayment;
    const userIsBetaEduzzBalance = eduzzAccountsService.verifyUserHasBetaModule('saldo_eduzz');
    const productIsBetaEduzzBalance = product.beta.includes('saldo_eduzz');
    const showEduzzBalance = (userIsBetaEduzzBalance || productIsBetaEduzzBalance) && (isEvent || isInfo);

    const paymentMethods = [
      {
        label: 'Permitir boleto',
        id: 'allows_boleto',
        checked: product.allows_boleto,
        helperText: 'Permitir pagamento por boleto no checkout?',
        show: true,
        error: 'allows_payment_types',
        disabled: isAlumy || product.trial,
        stateAlert: 'showAlertBoleto',
      },
      {
        label: 'Permitir cartão de crédito',
        id: 'allows_card',
        checked: product.allows_card,
        helperText: 'Permitir pagamento com cartão no checkout?',
        show: true,
        disabled: isAlumy,
        error: 'allows_payment_types',
        stateAlert: 'showAlertCard',
        ...(showInternationalChip && { chip: 'Disponível para venda internacional' }),
      },
      {
        label: 'Permitir múltiplos cartões',
        id: 'allows_multiple_cards',
        checked: product.allows_multiple_cards,
        helperText: 'Permitir pagamento com dois ou mais cartões no checkout?',
        disabled: !product.allows_card,
        show: !isSubscriptionPayment,
        stateAlert: 'showAlertMultipleCards',
      },
      {
        label: 'Permitir Pix',
        id: 'allows_pix',
        checked: product.allows_pix,
        helperText: 'Permitir pagamento com pix no checkout?',
        show: isSinglePayment || isSubscriptionPayment || isOpenValuePayment,
        error: 'allows_payment_types',
        stateAlert: 'showAlertPix',
      },
      {
        label: 'Permitir Saldo Eduzz',
        id: 'allows_eduzz_balance',
        checked: product.allows_eduzz_balance,
        helperText: (
          <p>
            Permitir pagamentos com Saldo Eduzz no checkout?{' '}
            <a
              target='_blank'
              rel='noopener noreferrer'
              href='https://ajuda.eduzz.com/hc/pt-br/articles/4406136094107-Como-pagar-uma-compra-com-saldo-dispon%C3%ADvel-para-transfer%C3%AAncia-pelo-Eduzz-Pay-Beta-'
            >
              Saiba mais
            </a>
          </p>
        ),
        show: showEduzzBalance,
        stateAlert: 'showAlertEduzzBalance',
      },
      {
        label: 'Parcelamento sem juros',
        id: 'interest_free_installment',
        checked: product.interest_free_installment,
        helperText: (
          <p>
            As parcelas no checkout irão aparecer sem os juros. Nesse caso o produtor assume os juros no lugar do comprador.{' '}
            <a
              target='_blank'
              rel='noopener noreferrer'
              href='https://ajuda.eduzz.com/hc/pt-br/articles/1260807551369-O-que-%C3%A9-Parcelamento-sem-juros-'
            >
              Saiba Mais
            </a>
          </p>
        ),
        show: !isFree && !isOpenValuePayment,
      },
      {
        label: 'Parcelamento sem limites',
        id: 'unlimited_installment',
        checked: product.unlimited_installment,
        helperText: (
          <p>
            Caso o seu cliente não tenha limite no cartão você pode oferecer o pagamento em parcelas recorrentes. Elas são cobradas mensalmente e sem
            comprometer o limite de crédito do comprador.{' '}
            <a target='_blank' rel='noopener noreferrer' href='https://ajuda.eduzz.com/hc/pt-br/articles/1260807548029-Parcelamento-Sem-Limites'>
              Saiba Mais
            </a>
          </p>
        ),
        show: isSinglePayment && !isPhysical,
      },
      {
        label: 'Entregar conteúdo apenas na primeira cobrança paga',
        id: 'deliver_first_charge',
        checked: product.deliver_first_charge,
        helperText: 'O cliente receberá a entrega do conteúdo apenas uma vez, ao realizar o pagamento da adesão ou da primeira cobrança.',
        show: isSinglePayment ? product.unlimited_installment : isSubscriptionPayment,
        disabled: isAlumy,
      },
    ] as PaymentMethod[];

    return (
      <>
        <Grid container spacing={2} alignItems='center'>
          <Grid item xs={12}>
            <Alert severity='info'>
              Ativamos nossas ferramentas para te ajudar a converter mais. Você pode desativá-las quando quiser, mas antes, confira os dados e
              impactos que levantamos para apoiar sua decisão.
            </Alert>
          </Grid>

          <Grid item xs={12}>
            <FormContent title='Meios de pagamento e checkout' loading={loading}>
              <Grid container spacing={2} alignItems='center'>
                {paymentMethods
                  .filter((pm) => pm.show)
                  .map((p, index) => {
                    const hasError = this.isValid(p.error);
                    const isFirst = index === 0;

                    const label = p.chip ? (
                      <>
                        {p.label} <Chip className={classes.chip} color='primary' size='small' label={p.chip} />
                      </>
                    ) : (
                      p.label
                    );
                    return (
                      <>
                        {!isFirst && (
                          <Grid item xs={12}>
                            <Divider />
                          </Grid>
                        )}
                        <Grid item xs={12} key={p.label}>
                          <FormControlLabel
                            label={label}
                            id={p.id}
                            disabled={p.disabled}
                            control={
                              <Switch
                                color='primary'
                                checked={p.checked}
                                onChange={(e: any) => {
                                  const willUncheck = !e.target.checked;

                                  if (willUncheck && paymentShouldConfirmToDisable.includes(p.id)) {
                                    return this.handleConfirmBeforeChange(p.id);
                                  }

                                  return handleChange(p.id, e.target.checked);
                                }}
                              />
                            }
                          />
                          {hasError && <FormHelperText style={{ color: 'red' }}>{this.errorFor(p.error)}</FormHelperText>}
                          {!hasError && <FormHelperText>{p.helperText}</FormHelperText>}
                        </Grid>
                      </>
                    );
                  })}
              </Grid>
              {isSinglePayment && isInfo && (
                <Grid container spacing={2} style={{ marginTop: 12 }}>
                  <Grid item xs={12}>
                    <Alert severity='warning'>
                      Para vendas feitas com moedas estrangeiras a opção de cartão de crédito será ativada automaticamente e as demais opções serão
                      desativadas
                    </Alert>
                  </Grid>
                </Grid>
              )}
            </FormContent>
          </Grid>
        </Grid>

        <AlertsDefaultOptions
          showAlert={{
            pix: this.state.showAlertPix,
            card: this.state.showAlertCard,
            boleto: this.state.showAlertBoleto,
            multipleCards: this.state.showAlertMultipleCards,
            eduzzBalance: this.state.showAlertEduzzBalance,
          }}
          toggleAlert={(alertName: AlertsDefaultOptionsStateName) => this.handleToggleAlertVisibility(alertName)}
          onToggleConfirmed={(fieldId: string, checked: boolean) => handleChange(fieldId, checked)}
        />
      </>
    );
  }
}
function mapStateToProps(state: any) {
  return {
    currentUser: state.orbita.User.user.user,
  };
}

export default connect(mapStateToProps)(styles(PaymentMethods));
